"use client"

import type React from "react"
import { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import {
  Upload, FileText, AlertCircle, CheckCircle, Loader2,
  ChevronDown, ChevronRight, ShieldCheck,
  AtSign, Server, Globe, Mail,
  CheckCircle2, XCircle,
} from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Progress } from "@/components/ui/progress"
import { apiFetch } from "@/lib/api/apiFetch"
import ImportResult, { ImportResultType } from "./ImportResult"
import { locationOptions } from "./contactsPage/ContactsHeader"
import { format } from "date-fns"
import { HybridSelect } from "./hybridSelect"
import * as XLSX from "xlsx"

// ─── Types ─────────────────────────────────────────────────────────────────────

interface ImportContactDialogProps {
  open: boolean
  statusOptionsList: any[]
  sourceOptionsList: any[]
  onOpenChange: (open: boolean) => void
  onContactsImported?: (contacts: any[]) => void
}

interface ParsedContact {
  company?: string
  firstName?: string
  lastName?: string
  phone?: string
  city?: string
  state?: string
  dateOfLastContact?: string
  interests?: string
  role?: string
  email: string
  source: string
  status: any
  stats?: EmailVerifyResult  // attached after verification
}

interface EmailVerifyResult {
  email: string
  risk_score: number
  verdict: string
  suggestion: string | null
  is_deliverable: boolean | null
  checks: {
    syntax: { valid: boolean; username: string; domain: string; error: string | null }
    disposable: { is_disposable: boolean; method: string; confidence: number }
    typo: { suggestion: string | null; distance: number | null }
    mx: { has_mx: boolean; records: string[]; error: string | null }
    smtp: any
    provider: any
  }
  cached: boolean
  checked_at: string
}

interface EmailVerifyResponse {
  success: boolean
  message: string
  code: string
  data: { total: number; results: EmailVerifyResult[] }
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const VERDICT_CONFIG: Record<string, {
  dot: string
  badge: string
  badgeText: string
  label: string
  textColor: string
  icon: React.ReactNode
}> = {
  safe: {
    dot: "bg-emerald-500",
    badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400",
    badgeText: "Safe",
    label: "Safe",
    textColor: "text-emerald-600 dark:text-emerald-400",
    icon: <CheckCircle2 className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />,
  },
  invalid: {
    dot: "bg-red-500",
    badge: "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400",
    badgeText: "Invalid",
    label: "Invalid",
    textColor: "text-red-600 dark:text-red-400",
    icon: <XCircle className="h-3 w-3 text-red-500 dark:text-red-400" />,
  },
}

function getVerdict(result: EmailVerifyResult | undefined) {
  if (!result) return null
  return VERDICT_CONFIG[result.verdict] ?? VERDICT_CONFIG["invalid"]
}

const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === "object" && error !== null) {
    const maybeMessage = (error as { message?: unknown }).message
    if (typeof maybeMessage === "string" && maybeMessage.trim()) return maybeMessage
  }
  return fallback
}

// ─── SettingsPanel ─────────────────────────────────────────────────────────────

interface SettingsPanelProps {
  selectedStatus: string
  selectedLocation: string
  selectedSource: string
  onStatusChange: (v: string) => void
  onLocationChange: (v: string) => void
  onSourceChange: (v: string) => void
  statusOptionsList: any[]
  sourceOptionsList: any[]
}

function SettingsPanel({
  selectedStatus, selectedLocation, selectedSource,
  onStatusChange, onLocationChange, onSourceChange,
  statusOptionsList, sourceOptionsList,
}: SettingsPanelProps) {

  const FieldSelect = ({
    label,
    value,
    onChange,
    options,
    placeholder,
  }: any) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options?.map((opt: any) => (
            <SelectItem key={opt.value || opt.label} value={opt.value || opt.label}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="p-4 rounded-lg bg-muted/30 border border-border space-y-3">

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* <FieldSelect
          label="Default Status"
          value={selectedStatus}
          onChange={onStatusChange}
          options={statusOptionsList}
          placeholder="Select status"
        /> */}
        <HybridSelect
          label="Default Status"
          value={selectedStatus}
          onChange={onStatusChange}
          options={statusOptionsList}
        />

        <FieldSelect
          label="Default Location"
          value={selectedLocation}
          onChange={onLocationChange}
          options={locationOptions}
          placeholder="Select location"
        />

        {/* <FieldSelect
          label="Default Source"
          value={selectedSource}
          onChange={onSourceChange}
          options={sourceOptionsList}
          placeholder="Select source"
        /> */}
        <HybridSelect
          label="Default Source"
          value={selectedSource}
          onChange={onSourceChange}
          options={sourceOptionsList}
        />
      </div>

      {/* NB Section (Aligned Properly) */}
      <div className="text-xs text-amber-600 dark:text-amber-400 flex items-start gap-2">
        <span className="font-semibold">NB:</span>
        <span>
          Default values will override existing data in your file for{" "}
          <b>status</b>, <b>source</b>, and <b>location</b>.
        </span>
      </div>
    </div>
  );
}

// ─── FileUploadZone ────────────────────────────────────────────────────────────

interface FileUploadZoneProps {
  selectedFile: File | null
  isDragActive: boolean
  fileInputRef: React.RefObject<HTMLInputElement | null>
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void
}

function FileUploadZone({
  selectedFile, isDragActive, fileInputRef,
  onFileSelect, onDragOver, onDragLeave, onDrop,
}: FileUploadZoneProps) {
  return (
    <div className="space-y-3">
      <Label>Upload CSV or Excel File</Label>
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${isDragActive
          ? "border-primary bg-primary/5"
          : "border-border hover:border-primary/50 hover:bg-muted/20"
          }`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className={`h-9 w-9 mx-auto mb-3 ${isDragActive ? "text-primary" : "text-muted-foreground"}`} />
        <p className="text-sm font-medium text-foreground mb-1">
          {isDragActive ? "Drop your file here" : "Click to upload or drag and drop"}
        </p>
        <p className="text-xs text-muted-foreground">CSV, XLS, XLSX — max 10MB</p>
        <Input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xls,.xlsx"
          onChange={onFileSelect}
          className="hidden"
        />
        {selectedFile && (
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted border border-border">
            <FileText className="h-4 w-4 text-primary shrink-0" />
            <span className="text-sm font-medium text-foreground">{selectedFile.name}</span>
            <span className="text-xs text-muted-foreground">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
          </div>
        )}
      </div>

      <Alert className="bg-muted/50 border-border">
        <AlertCircle className="h-4 w-4 text-muted-foreground" />
        <AlertDescription className="text-xs text-muted-foreground">
          <strong className="text-foreground">Required columns:</strong> first name, email. &nbsp;
          Optional: last name, company, phone, city, state, source, status, role, date of contact, interests.
        </AlertDescription>
      </Alert>

      <div className="flex justify-center">
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            const link = document.createElement("a")
            link.href = "/api/contacts/sample-csv"
            link.download = "contacts-template.csv"
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          }}
        >
          <FileText className="h-4 w-4 mr-2" />
          Download CSV Template
        </Button>
      </div>
    </div>
  )
}

// ─── VerifySummaryBar ──────────────────────────────────────────────────────────

function VerifySummaryBar({ safe, invalid }: { safe: number; invalid: number }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-border bg-muted/30 text-sm">
      <ShieldCheck className="h-4 w-4 text-primary shrink-0" />
      <span className="font-medium text-foreground">Verification complete</span>
      <div className="ml-auto flex items-center gap-4 text-xs">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-emerald-500 inline-block shrink-0" />
          <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{safe} safe</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-red-500 inline-block shrink-0" />
          <span className="text-red-600 dark:text-red-400 font-semibold">{invalid} invalid</span>
        </span>
      </div>
    </div>
  )
}

// ─── VerifyExpandedRow ─────────────────────────────────────────────────────────

function VerifyExpandedRow({ result, colSpan }: { result: EmailVerifyResult; colSpan: number }) {
  const vc = getVerdict(result)!

  const CheckItem = ({ icon, label, pass, sub }: {
    icon: React.ReactNode; label: string; pass: boolean; sub?: string
  }) => (
    <div className="flex items-start gap-2">
      <div className="shrink-0 text-muted-foreground">{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium leading-none mb-1">{label}</p>
        <div className={`text-xs font-semibold flex items-center gap-1 ${pass ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
          {pass ? <CheckCircle2 className="h-3 w-3 shrink-0" /> : <XCircle className="h-3 w-3 shrink-0" />}
          {pass ? "Pass" : "Fail"}
        </div>
        {sub && <p className="text-xs text-muted-foreground/70 mt-0.5 break-all">{sub}</p>}
      </div>
    </div>
  )

  const RiskBar = ({ score }: { score: number }) => {
    const color = score === 0 ? "bg-emerald-500" : "bg-red-500"
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.max(score, 3)}%` }} />
        </div>
        <span className="text-xs font-bold text-muted-foreground w-5 text-right shrink-0">{score}</span>
      </div>
    )
  }

  return (
    <tr className="bg-muted/20 dark:bg-muted/10 border-t border-border/40">
      <td colSpan={colSpan} className="px-5 py-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4">
          {/* Verdict + risk score */}
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium leading-none">Verdict</p>
            <div className={`flex items-center gap-1.5 text-xs ${vc.textColor}`}>
              {vc.icon} {vc.label}
            </div>
            <RiskBar score={result.risk_score} />
          </div>
          <CheckItem
            icon={<AtSign className="h-3.5 w-3.5" />}
            label="Syntax"
            pass={result.checks.syntax.valid}
            sub={`${result.checks.syntax.username}@${result.checks.syntax.domain}`}
          />
          <CheckItem
            icon={<Mail className="h-3.5 w-3.5" />}
            label="Disposable"
            pass={!result.checks.disposable.is_disposable}
            sub={`Confidence ${Math.round(result.checks.disposable.confidence * 100)}%`}
          />
          <CheckItem
            icon={<Server className="h-3.5 w-3.5" />}
            label="MX Records"
            pass={result.checks.mx.has_mx}
            sub={result.checks.mx.records?.[0]}
          />
        </div>

        <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-border/40">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Globe className="h-3 w-3 shrink-0" />
            {result.checks.typo.suggestion
              ? <span className="text-amber-600 dark:text-amber-400">
                Typo? Did you mean: <strong>{result.checks.typo.suggestion}</strong>
              </span>
              : <span>No typo suggestions</span>
            }
          </div>
          <div className="flex items-center gap-2 ml-4 shrink-0">
            {result.cached && <Badge variant="secondary" className="text-xs px-1.5 py-0">Cached</Badge>}
            <span className="text-xs text-muted-foreground">
              {format(new Date(result.checked_at), "MMM d · h:mm a")}
            </span>
          </div>
        </div>
      </td>
    </tr>
  )
}

// ─── ContactPreviewRow ─────────────────────────────────────────────────────────

function ContactPreviewRow({ contact, verifyResult, isExpanded, onToggle, defaultStatus, defaultSource }: {
  contact: ParsedContact
  verifyResult: EmailVerifyResult | undefined
  isExpanded: boolean
  onToggle: () => void
  defaultStatus?: string
  defaultSource?: string
}) {
  const vc = getVerdict(verifyResult)
  const canExpand = !!verifyResult
  const dash = <span className="text-muted-foreground/50">—</span>

  return (
    <>
      <tr
        className={`border-t border-border transition-colors ${canExpand ? "hover:bg-muted/40 cursor-pointer" : ""}`}
        onClick={canExpand ? onToggle : undefined}
      >
        {/* Indicator dot */}
        <td className="pl-3 pr-1 py-2.5 w-6">
          {vc
            ? <span className={`block h-2 w-2 rounded-full ${vc.dot}`} title={vc.label} />
            : <span className="block h-2 w-2 rounded-full bg-muted-foreground/25" title="Not verified" />
          }
        </td>
        {/* Expand chevron */}
        <td className="px-1 py-2.5 w-5">
          {canExpand && (
            isExpanded
              ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </td>
        <td className="px-3 py-2.5 whitespace-nowrap text-sm">{contact.company || dash}</td>
        <td className="px-3 py-2.5 whitespace-nowrap text-sm">{contact.firstName || dash}</td>
        <td className="px-3 py-2.5 whitespace-nowrap text-sm">{contact.lastName || dash}</td>
        <td className="px-3 py-2.5 whitespace-nowrap text-sm">
          <span className="flex items-center gap-2">
            <span>{contact.email}</span>
            {vc && (
              <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full shrink-0 ${vc.badge}`}>
                {vc.badgeText}
              </span>
            )}
          </span>
        </td>
        <td className="px-3 py-2.5 whitespace-nowrap text-sm">{contact.phone || dash}</td>
        <td className="px-3 py-2.5 whitespace-nowrap text-sm">{contact.city || dash}</td>
        <td className="px-3 py-2.5 whitespace-nowrap text-sm">{contact.state || dash}</td>
        <td className="px-3 py-2.5 whitespace-nowrap text-sm">{defaultSource || contact.source || dash}</td>
        <td className="px-3 py-2.5 whitespace-nowrap text-sm">{defaultStatus || contact.status?.name || contact.status || dash}</td>
        <td className="px-3 py-2.5 whitespace-nowrap text-sm">{contact.role || "User"}</td>
      </tr>
      {isExpanded && verifyResult && (
        <VerifyExpandedRow result={verifyResult} colSpan={12} />
      )}
    </>
  )
}

// ─── ContactsTable ─────────────────────────────────────────────────────────────

interface ContactsTableProps {
  contacts: ParsedContact[]
  allContacts: ParsedContact[]
  verifyMap: Record<string, EmailVerifyResult>
  expandedRows: Set<string>
  showAll: boolean
  showToggle?: boolean
  onToggleRow: (email: string) => void
  onToggleShowAll: () => void
  defaultStatus?: string
  defaultSource?: string
}

function ContactsTable({
  contacts, allContacts, verifyMap, expandedRows, showAll, showToggle = true, onToggleRow, onToggleShowAll,
  defaultStatus, defaultSource
}: ContactsTableProps) {
  const hasVerify = Object.keys(verifyMap).length > 0
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" />
        <Label className="font-medium">
          {allContacts.length} contacts found
          {hasVerify && (
            <span className="text-muted-foreground font-normal"> · click any row to see verification details</span>
          )}
        </Label>
      </div>

      {/*
        Key fix for responsive table:
        - Outer wrapper: w-full, overflow-x-auto (horizontal scroll)
        - Inner wrapper: max-h for vertical scroll
        - Table: min-w-max so it never shrinks below its natural width
      */}
      {/* overflow-x-auto on the outer so horizontal scroll works without fighting parent */}
      <div className="w-full rounded border border-border overflow-x-auto">
        <div className="overflow-y-auto max-h-[280px]">
          <table className="min-w-max text-sm">
            <thead className="sticky top-0 bg-background z-10 shadow-[0_1px_0_0_hsl(var(--border))]">
              <tr>
                <th className="pl-3 pr-1 py-2.5 w-6" />
                <th className="px-1 py-2.5 w-5" />
                <th className="px-3 py-2.5 text-left font-medium text-muted-foreground whitespace-nowrap text-xs uppercase tracking-wide">Company</th>
                <th className="px-3 py-2.5 text-left font-medium text-muted-foreground whitespace-nowrap text-xs uppercase tracking-wide">First Name</th>
                <th className="px-3 py-2.5 text-left font-medium text-muted-foreground whitespace-nowrap text-xs uppercase tracking-wide">Last Name</th>
                <th className="px-3 py-2.5 text-left font-medium text-muted-foreground whitespace-nowrap text-xs uppercase tracking-wide">Email</th>
                <th className="px-3 py-2.5 text-left font-medium text-muted-foreground whitespace-nowrap text-xs uppercase tracking-wide">Phone</th>
                <th className="px-3 py-2.5 text-left font-medium text-muted-foreground whitespace-nowrap text-xs uppercase tracking-wide">City</th>
                <th className="px-3 py-2.5 text-left font-medium text-muted-foreground whitespace-nowrap text-xs uppercase tracking-wide">State</th>
                <th className="px-3 py-2.5 text-left font-medium text-muted-foreground whitespace-nowrap text-xs uppercase tracking-wide">Source</th>
                <th className="px-3 py-2.5 text-left font-medium text-muted-foreground whitespace-nowrap text-xs uppercase tracking-wide">Status</th>
                <th className="px-3 py-2.5 text-left font-medium text-muted-foreground whitespace-nowrap text-xs uppercase tracking-wide">Role</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact, i) => (
                <ContactPreviewRow
                  key={contact.email + i}
                  contact={contact}
                  verifyResult={verifyMap[contact.email]}
                  isExpanded={expandedRows.has(contact.email)}
                  onToggle={() => onToggleRow(contact.email)}
                  defaultStatus={defaultStatus}
                  defaultSource={defaultSource}
                />
              ))}
            </tbody>
          </table>
        </div>

        {showToggle && allContacts.length > 10 && (
          <div className="py-2.5 text-center border-t border-border bg-background">
            <button
              onClick={onToggleShowAll}
              className="text-primary hover:underline text-sm font-medium"
            >
              {showAll ? "Show less" : `Show ${allContacts.length - 10} more`}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── HighRiskCheckbox ──────────────────────────────────────────────────────────

interface HighRiskCheckboxProps {
  checked: boolean
  onCheckedChange: (v: boolean) => void
  totalCount: number
  safeCount: number
}

function HighRiskCheckbox({ checked, onCheckedChange, totalCount, safeCount }: HighRiskCheckboxProps) {
  const skippedCount = totalCount - safeCount
  return (
    <div className="flex items-start gap-3 px-4 py-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30">
      <Checkbox
        id="importHighRisk"
        checked={checked}
        onCheckedChange={(v) => onCheckedChange(!!v)}
        className="mt-1.5 shrink-0"
      />
      <div className="space-y-0.5">
        <Label htmlFor="importHighRisk" className="text-sm font-medium cursor-pointer">
          Import contacts with high risk scores
        </Label>
        <p className="text-xs text-muted-foreground">
          {checked
            ? `All ${totalCount} contacts will be imported, including invalid emails.`
            : skippedCount > 0
              ? `${skippedCount} invalid contact${skippedCount !== 1 ? "s" : ""} will be skipped — only ${safeCount} safe contact${safeCount !== 1 ? "s" : ""} will be imported.`
              : `All ${safeCount} contacts are safe and will be imported.`
          }
        </p>
      </div>
    </div>
  )
}

// ─── ImportContactDialog ───────────────────────────────────────────────────────

export function ImportContactDialog({
  open, onOpenChange, onContactsImported, statusOptionsList, sourceOptionsList,
}: ImportContactDialogProps) {
  // Settings
  const [selectedStatus, setSelectedStatus] = useState("")
  const [selectedLocation, setSelectedLocation] = useState("")
  const [selectedSource, setSelectedSource] = useState("")
  const [overwrite, setOverwrite] = useState(false)
  const [isCreateList, setIsCreateList] = useState(true)
  const [listName, setListName] = useState("")

  // File
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDragActive, setIsDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Contacts
  const [parsedContacts, setParsedContacts] = useState<ParsedContact[]>([])
  const [parseError, setParseError] = useState<ParseError[] | null>(null)
  const [showAll, setShowAll] = useState(false)

  // Verification
  const [verifyMap, setVerifyMap] = useState<Record<string, EmailVerifyResult>>({})
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [importHighRisk, setImportHighRisk] = useState(false)
  const [verifyDone, setVerifyDone] = useState(false)
  const isListNameValid = !isCreateList || listName.trim().length > 0

  // Loading
  const [isVerifying, setIsVerifying] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [importStep, setImportStep] = useState(0)

  // Result
  const [importResult, setImportResult] = useState<ImportResultType | null>(null)

  const { toast } = useToast()

  const importMessages = [
    "", "Preparing your contacts...", "Validating contact information...",
    "Organizing data for import...", "Saving contacts to database...",
    "Updating records...", "Finalizing...", "Almost done...",
  ]

  useEffect(() => {
    if (uploadProgress === 100) {
      let step = 1
      const interval = setInterval(() => {
        setImportStep(step); step++
        if (step >= importMessages.length) clearInterval(interval)
      }, 1200)
      return () => clearInterval(interval)
    }
  }, [uploadProgress])

  // ── Derived ───────────────────────────────────────────────────────────────

  const showTable = parsedContacts.length > 0
  const safeContacts = parsedContacts.filter((c) => verifyMap[c.email]?.verdict === "safe")
  const notSafeContacts = parsedContacts.filter((c) => verifyMap[c.email]?.verdict !== "safe")

  const verifyStats = verifyDone ? {
    safe: parsedContacts.filter((c) => verifyMap[c.email]?.verdict === "safe").length,
    invalid: parsedContacts.filter((c) => verifyMap[c.email]?.verdict === "invalid").length,
  } : null

  const safeCount = verifyStats?.safe ?? parsedContacts.length
  const importCount = verifyDone && !importHighRisk
    ? parsedContacts.filter((c) => { const v = verifyMap[c.email]; return !v || v.verdict === "safe" }).length
    : parsedContacts.length

  // ── File handling ─────────────────────────────────────────────────────────

  const validateAndSetFile = (file: File) => {
    const valid = ["text/csv", "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"]
    if (!valid.includes(file.type) && !file.name.match(/\.(csv|xlsx|xls)$/)) {
      toast({ title: "Invalid file type", description: "Please select a CSV or Excel file", variant: "destructive" })
      return
    }
    setSelectedFile(file)
    setParseError(null)
    setParsedContacts([])
    setVerifyMap({})
    setVerifyDone(false)
    setExpandedRows(new Set())
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (file) validateAndSetFile(file)
  }
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); setIsDragActive(true) }
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); e.stopPropagation(); setIsDragActive(false) }
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation(); setIsDragActive(false)
    const file = e.dataTransfer.files?.[0]; if (file) validateAndSetFile(file)
  }

  // ── CSV parsing ───────────────────────────────────────────────────────────

  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

  type ParseError = {
    row: number
    email?: string
    error: string
  }

  const normalize = (key: string) =>
    key?.toLowerCase().trim()

  const parseFile = async (
    file: File
  ): Promise<{ contacts: ParsedContact[]; errors: ParseError[] }> => {
    const data = await file.arrayBuffer()
    const workbook = XLSX.read(data, { type: "array" })

    const sheetName = workbook.SheetNames[0]
    const sheet = workbook.Sheets[sheetName]

    const json: any[] = XLSX.utils.sheet_to_json(sheet, {
      defval: "", // empty cells = ""
    })

    const contacts: ParsedContact[] = []
    const errors: ParseError[] = []

    json.forEach((row, index) => {
      const r: Record<string, any> = {}

      // normalize keys (important)
      Object.keys(row).forEach((key) => {
        r[normalize(key)] = row[key]
      })

      const email = r["email"]?.trim().toLowerCase()
      const firstName = r["first_name"]?.trim()

      if (!firstName || !email) {
        errors.push({
          row: index + 2,
          email,
          error: "Missing first_name or email",
        })
        return
      }

      if (!isValidEmail(email)) {
        errors.push({
          row: index + 2,
          email,
          error: "Invalid email",
        })
        return
      }

      contacts.push({
        email,
        firstName,
        lastName: r["last_name"] || "",
        company: r["company"] || "",
        phone: r["phone"] || "",
        city: r["city"] || "",
        state: r["state"] || "",
        source: r["source"] || "",
        status: r["status"] || "",
        role: r["role"] || "",
        dateOfLastContact: r["date_of_last_contact"] || "",
        interests: r["interest"] || "",
      })
    })

    return { contacts, errors }
  }

  // ── Step 1: Verify & Preview (single action) ──────────────────────────────

  const handlePreview = async () => {
    if (!selectedFile) return

    setIsUploading(true)
    setUploadProgress(20)

    try {

      setUploadProgress(60)

      const { contacts, errors } = await parseFile(selectedFile)

      setUploadProgress(80)

      if (contacts.length === 0) {
        throw new Error("No valid contacts found in the file")
      }

      setParsedContacts(contacts)
      setParseError(errors)

      setUploadProgress(100)

      toast({
        title: "File parsed",
        description:
          errors.length > 0
            ? `${contacts.length} contacts imported, ${errors.length} rows skipped`
            : `Found ${contacts.length} valid contacts`,
      })
    } catch (error) {
      toast({
        title: "Parse error",
        description: error instanceof Error ? error.message : "Failed to parse file",
        variant: "destructive",
      })
    } finally {
      setIsUploading(false)
      setTimeout(() => setUploadProgress(0), 1000)
    }
  }

  // ── Step 2: Import ─────────────────────────────────────────────────────────

  const handleUpload = async () => {
    if (!parsedContacts.length) return
    setIsUploading(true)
    setUploadProgress(0)
    try {
      for (let i = 0; i <= 100; i += 10) {
        setUploadProgress(i)
        await new Promise((r) => setTimeout(r, 100))
      }

      // Each contact already has its stats field attached from verification step
      const result = await apiFetch("/api/contacts/bulk-import", {
        method: "POST",
        data: {
          contacts: parsedContacts,
          overwrite,
          defaultStatus: statusOptionsList.find(o => o.value === selectedStatus)?.label || selectedStatus,
          defaultLocation: selectedLocation,
          defaultSource: sourceOptionsList.find(o => o.value === selectedSource)?.label || selectedSource,
          ...(listName?.trim() ? { listName: listName.trim() } : {}),
        },
        timeoutMs: 240000,
      })
      toast({ title: "Import successful", description: `Imported ${parsedContacts.length} contacts` })
      onContactsImported?.(parsedContacts)
      setImportResult(result)
      setParsedContacts([])
      setSelectedFile(null)
    } catch (error) {
      toast({
        title: "Import failed",
        description: getApiErrorMessage(error, "Failed to import contacts"),
        variant: "destructive",
      })
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  const toggleRow = (email: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      next.has(email) ? next.delete(email) : next.add(email)
      return next
    })
  }

  const resetForm = () => {
    setSelectedFile(null); setParsedContacts([]); setParseError(null)
    setUploadProgress(0); setListName(""); setIsCreateList(true)
    setVerifyMap({}); setVerifyDone(false); setExpandedRows(new Set())
    setImportHighRisk(false); setImportResult(null); setImportStep(0)
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const defaultStatusLabel = statusOptionsList.find(o => o.value === selectedStatus)?.label || selectedStatus
  const defaultSourceLabel = sourceOptionsList.find(o => o.value === selectedSource)?.label || selectedSource

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) resetForm() }}>
      <DialogContent className="sm:max-w-2xl md:max-w-4xl flex flex-col max-h-[85vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Import Contacts</DialogTitle>
        </DialogHeader>

        {importResult ? (
          <ImportResult result={importResult} onBack={() => setImportResult(null)} />
        ) : (
          <div className="space-y-5 overflow-y-auto flex-1 pr-1">

            {/* ── Settings ── */}
            <SettingsPanel
              selectedStatus={selectedStatus} selectedLocation={selectedLocation} selectedSource={selectedSource}
              onStatusChange={setSelectedStatus} onLocationChange={setSelectedLocation} onSourceChange={setSelectedSource}
              statusOptionsList={statusOptionsList} sourceOptionsList={sourceOptionsList}
            />

            {/* ── Upload zone (hidden once contacts are loaded) ── */}
            {!showTable && (
              <FileUploadZone
                selectedFile={selectedFile} isDragActive={isDragActive} fileInputRef={fileInputRef}
                onFileSelect={handleFileSelect} onDragOver={handleDragOver}
                onDragLeave={handleDragLeave} onDrop={handleDrop}
              />
            )}

            {/* ── Inline loading states ── */}
            {isVerifying && (
              <div className="flex items-center gap-2.5 text-sm text-muted-foreground py-1">
                <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
                <span>Parsing and verifying emails in <strong>{selectedFile?.name}</strong>…</span>
              </div>
            )}

            {isUploading && (
              <div className="space-y-2">
                {uploadProgress < 100 ? (
                  <>
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Importing contacts…</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <Progress value={uploadProgress} className="w-full" />
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                    <span className="animate-pulse">{importMessages[importStep]}</span>
                  </div>
                )}
              </div>
            )}

            {/* ── Parse error ── */}
            {/* {parseError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{parseError}</AlertDescription>
              </Alert>
            )} */}

            {/* ── Verify summary bar ── */}
            {verifyDone && verifyStats && (
              <VerifySummaryBar safe={verifyStats.safe} invalid={verifyStats.invalid} />
            )}

            {/* ── Contacts table ── */}
            {showTable && (
              <>
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                      Preview Contacts
                    </Label>
                    <ContactsTable
                      contacts={parsedContacts}
                      allContacts={parsedContacts}
                      verifyMap={verifyMap}
                      expandedRows={expandedRows}
                      showAll={false}
                      showToggle={false}
                      onToggleRow={toggleRow}
                      onToggleShowAll={() => setShowAll((p) => !p)}
                      defaultStatus={defaultStatusLabel}
                      defaultSource={defaultSourceLabel}
                    />
                  </div>

                  {/* ── Parse error mapping table ── */}
                  {parseError && parseError.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-red-600 dark:text-red-400 flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          Skipped Rows & Issues
                        </Label>
                        <Badge variant="outline" className="text-[10px] uppercase font-bold border-red-200 text-red-600 dark:border-red-800 dark:text-red-400 bg-red-50/50 dark:bg-red-900/10">
                          {parseError.length} Errors detected
                        </Badge>
                      </div>
                      <div className="w-full rounded-2xl border border-red-100 dark:border-red-900/30 overflow-hidden bg-red-50/5 border-dashed">
                        <div className="overflow-y-auto max-h-[180px]">
                          <table className="w-full text-left border-collapse">
                            <thead className="bg-red-50/50 dark:bg-red-900/20 sticky top-0 font-bold text-red-800 dark:text-red-300 border-b border-red-100 dark:border-red-900/30 backdrop-blur-md z-10">
                              <tr>
                                <th className="px-4 py-3 w-20 text-[10px] uppercase tracking-wider">Row #</th>
                                <th className="px-4 py-3 w-64 text-[10px] uppercase tracking-wider">Attempted Email</th>
                                <th className="px-4 py-3 text-[10px] uppercase tracking-wider">Error Insight</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-red-100/50 dark:divide-red-950/20">
                              {parseError.map((err, i) => (
                                <tr key={i} className="hover:bg-red-500/5 transition-all group">
                                  <td className="px-4 py-3 font-bold text-sm text-red-700/80 dark:text-red-400/80">{err.row}</td>
                                  <td className="px-4 py-3 font-mono text-[11px] break-all text-zinc-500 dark:text-zinc-400 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                                    {err.email || <span className="italic opacity-30">Null / Empty Data</span>}
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800/50 shadow-sm">
                                      {err.error}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* High risk checkbox — only after verification */}
                {verifyDone && (
                  <HighRiskCheckbox
                    checked={importHighRisk}
                    onCheckedChange={setImportHighRisk}
                    totalCount={parsedContacts.length}
                    safeCount={safeCount}
                  />
                )}
                <div className="flex justify-between">


                  {/* Overwrite switch */}
                  <div className="flex items-center gap-3 pt-5 border-t border-border">
                    <Switch checked={overwrite} onCheckedChange={setOverwrite} id="overwrite" />
                    <Label htmlFor="overwrite" className="text-sm text-muted-foreground cursor-pointer font-normal">
                      Update existing contacts if they already exist
                    </Label>
                  </div>
                  {/* right — Create List (visible once contacts are loaded) */}
                  <div className="flex items-center gap-3">
                    {showTable && (
                      <>
                        <div className="flex items-center gap-2">
                          <Switch checked={isCreateList} onCheckedChange={setIsCreateList} id="createList" />
                          <Label htmlFor="createList" className="text-sm cursor-pointer whitespace-nowrap">Create List</Label>
                        </div>
                        {isCreateList && (
                          <div className="flex flex-col gap-1.5 w-52">
                            <Label htmlFor="listName" className="text-xs text-muted-foreground">
                              List Name <span className="text-red-500">*</span>
                            </Label>

                            <div className="relative">
                              <Input
                                id="listName"
                                autoFocus
                                type="text"
                                placeholder="e.g. April Leads"
                                value={listName}
                                onChange={(e) => setListName(e.target.value)}
                                className={`pr-9 ${!listName.trim()
                                  ? "border-red-400 focus-visible:ring-red-400"
                                  : "focus-visible:ring-emerald-500"
                                  }`}
                              />

                              {/* Right icon feedback */}
                              {listName.trim() ? (
                                <CheckCircle2 className="absolute right-2 top-2.5 h-4 w-4 text-emerald-500" />
                              ) : (
                                <AlertCircle className="absolute right-2 top-2.5 h-4 w-4 text-red-400" />
                              )}
                            </div>

                            {/* Helper / error text */}
                            <p
                              className={`text-xs ${listName.trim()
                                ? "text-muted-foreground"
                                : "text-red-500"
                                }`}
                            >
                              {listName.trim()
                                ? "This list will group imported contacts"
                                : "List name is required"}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* ── Action bar ── */}
            <div className="flex items-center justify-between gap-3 border-t border-border pt-5">



              {/* Right — Action buttons */}
              <div className="flex items-center gap-2 ml-auto">
                <Button
                  variant="outline"
                  onClick={() => { onOpenChange(false); resetForm() }}
                  disabled={isVerifying || isUploading}
                >
                  Cancel
                </Button>

                {/* Step 1 — Verify & Preview */}
                {!showTable && (
                  <Button
                    onClick={handlePreview}
                    disabled={!selectedFile || isVerifying}
                    className="gap-2"
                  >
                    {isVerifying
                      ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifying…</>
                      : <> Preview</>
                    }
                  </Button>
                )}

                {/* Step 2 — Import */}
                {showTable && (
                  <Button
                    onClick={handleUpload}
                    disabled={isUploading || isVerifying || importCount === 0 || (isCreateList && !isListNameValid)}
                    className="gap-2"
                  >
                    {isUploading
                      ? <><Loader2 className="h-4 w-4 animate-spin" /> Importing…</>
                      : `Import ${importCount} Contact${importCount !== 1 ? "s" : ""}`
                    }
                  </Button>
                )}
              </div>
            </div>

          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}